<template>
  <div class="login-form">
    <h1 class="login-form__title">
      {{
        activeKey === 'login'
          ? $t('huan-ying-deng-lu-you-yan-fu-wu-ping-tai')
          : $t('chuang-jian-yi-ge-zhang-hu')
      }}
    </h1>

    <div class="sub" v-if="!$isPure">
      <span class="sub-item" v-if="activeKey === 'login'">
        <div>{{ $t('mei-you-zhang-hao') }}</div>
        <Text variant="12" color="p6" class="cursor-pointer" @click="onToggleKey">
          {{ $t('li-ji-zhu-ce') }}
        </Text>
      </span>
      <span class="sub-item" v-if="activeKey === 'register'">
        <div>{{ $t('yi-you-zhang-hao') }}</div>
        <Text variant="12" color="p6" class="cursor-pointer" @click="onToggleKey">
          {{ $t('li-ji-deng-lu') }}
        </Text>
      </span>
    </div>

    <Form v-model:form-ref="formRef" class="login-form__form" @submit="onFinish">
      <template v-if="activeKey === 'register'">
        <FormField v-model="formState.email" name="email">
          <InputField :placeholder="$t('qing-shu-ru-you-xiang')" :height="44" class="login-input" />
        </FormField>
        <FormField v-model="formState.sms_code" name="sms_code">
          <CaptchaInput
            v-model="formState.sms_code"
            :placeholder="$t('qing-shu-ru-yan-zheng-ma')"
            :height="44"
            :is-send="isSend"
            :times="times"
            :button-text="$t('huo-qu-yan-zheng-ma')"
            :button-text-resend="time => $t('zhong-xin-huo-qu-times-s', [time])"
            input-class="login-input login-form__captcha-input"
            button-class="login-form__captcha-button"
            @send-code="handleSendCode"
          />
        </FormField>
        <FormField v-model="formState.password" name="password">
          <InputField
            type="password"
            :placeholder="$t('xin-mi-ma')"
            :height="44"
            show-password-toggle
            class="login-input"
          />
          <div
            :class="{
              'pwd-rule-tip': true,
              'pwd-rule-tip--success': PwdValidator.isSafeRange(formState.password),
            }"
          >
            <component
              :is="PwdValidator.isSafeRange(formState.password) ? CheckIcon : CloseSmallIcon"
              class="pwd-rule-tip-icon"
            />
            <Text variant="14" color="t5">
              {{ $t('chang-du-wei-620-ge-zi-fu') }}
            </Text>
          </div>
          <div
            :class="{
              'pwd-rule-tip': true,
              'pwd-rule-tip--success': PwdValidator.isValidFormat(formState.password),
            }"
          >
            <component
              :is="PwdValidator.isValidFormat(formState.password) ? CheckIcon : CloseSmallIcon"
              class="pwd-rule-tip-icon"
            />
            <Text variant="14" color="t5">
              {{ $t('bao-han-shu-zi-da-xie-zi-mu-xiao-xie-zi-fu-zi-fu-san-zhong-ji-yi-shang') }}
            </Text>
          </div>
        </FormField>
        <FormField v-model="formState.checkPassword" name="checkPassword">
          <InputField
            type="password"
            :placeholder="$t('qing-que-ren-mi-ma')"
            :height="44"
            show-password-toggle
            class="login-input"
          />
        </FormField>
      </template>

      <template v-if="activeKey === 'login'">
        <FormField v-model="formState.email" name="email">
          <InputField :placeholder="$t('qing-shu-ru-you-xiang')" :height="44" class="login-input" />
        </FormField>
        <FormField v-model="formState.password" name="password">
          <InputField
            type="password"
            :placeholder="$t('qing-shu-ru-mi-ma')"
            :height="44"
            show-password-toggle
            class="login-input"
          />
        </FormField>
      </template>

      <div :class="['login-form__buttons', activeKey === 'login' ? 'show-forget-password' : '']">
        <Button
          variant="purple"
          size="large"
          :disabled="!canSubmit"
          type="submit"
          :loading="loading"
        >
          {{ activeKey === 'login' ? $t('deng-lu') : $t('zhu-ce') }}
        </Button>

        <Button
          v-if="activeKey === 'login' && !$isPure"
          variant="text-purple"
          :class="['login-form__forget-password']"
          @click="onOpenReset"
        >
          {{ $t('wang-ji-mi-ma') }}
        </Button>
      </div>

      <div class="login-form__divider" v-if="!$isPure">
        <span>{{ $t('or-continue-with') }}</span>
      </div>

      <GoogleLogin
        v-if="!$isPure"
        class="google-login-btn"
        :disabled="!formState.remember"
        @success="handleGoogleLoginSuccess"
      />
    </Form>

    <ProtocolCheckbox
      v-if="!$isPure"
      :use-checkbox="false"
      v-model="formState.remember"
      user-agreement-url="https://protocol.rendora.ai/legal/user"
      privacy-policy-url="https://protocol.rendora.ai/legal/privacy-policy"
      user-agreement-text="User Agreement"
      privacy-policy-text="Privacy Policy"
      :prefix-text="$t('by-signing-up-to-the-platform-you-understand-and-agree-to-our')"
      :middle-text="$t('huo')"
    />
  </div>
</template>
<script lang="ts" setup>
import { getEmailCode, getUserInfo, loginByEmail, registerByEmail } from '@/api';
import { useSendCode } from '@/hooks/useSendCode';
import { message } from '@rendora/common/composables/message';
import type { Account } from '@rendora/common/entities/user';
import { useUserStore } from '@rendora/common/store';
import CloseSmallIcon from '@rendora/design/assets/circle-error.svg?component';
import CheckIcon from '@rendora/design/assets/circle-success.svg?component';
import { Button } from '@rendora/design/components/button';
import { Form, FormField } from '@rendora/design/components/form';
import { InputField } from '@rendora/design/components/input';
import { Text } from '@rendora/design/components/text';
import CaptchaInput from '@rendora/design/widgets/captcha-input/index.vue';
import { useI18n } from 'vue-i18n';
import type { LoginInfo } from '~/api/interface';
import { withRegisterOrLoginChannelForUs } from '~/api/module/open-login';
import { getLocation } from '~/api/module/seo';
import { ServerErrorCode } from '~/api/request';
import { BROADCAST_KEY, SESSION_SOURCE } from '~/constants/config';
import { useTime } from '~/hooks/useTime';
import { concurrency } from '~/utils/concurrency';
import { PwdValidator, transformPublicKey } from '~/utils/form';
import { sessionRemoveItem } from '~/utils/localStorage';
import { emitBroadcast, TAB_ID } from '~/utils/postMessage';
import { emailReg } from '~/utils/regExp';
import { buriedLogin, LOGIN_WAY, TRACK_EVENT, trackEvent, USER_TYPE } from '~/utils/track';
import { useProtocolConfirm } from '../../../shared/lib/useProtocolConfirm';
import { trackData } from '../lib/trackData';
import GoogleLogin from './LoginGoogle.vue';
import ProtocolCheckbox from './ProtocolCheckbox.vue';

const { show: showProtocolConfirm } = useProtocolConfirm();
const { t: $t } = useI18n();
const { $isPure } = useNuxtApp();
const { sendCode, isSend, times } = useSendCode();

interface FormState {
  email: string;
  // email: string
  checkPassword: string;
  sms_code: string;
  password: string;
  remember: boolean;
}

const formState = reactive<FormState>({
  email: '',
  sms_code: '',
  password: '',
  checkPassword: '',
  remember: true,
});
const { setAccountList, setSession, setUserInfo } = useChangeUser();
const loading = ref(false);
const formRef = ref();
const emits = defineEmits(['back', 'success', 'accountSelect']);

type LoginType = 'register' | 'login';

const activeKey = ref<LoginType>('login');

const onToggleKey = () => {
  trackEvent(TRACK_EVENT.YOUYAN_LOGIN_CLICK_REGISTER);
  activeKey.value = activeKey.value !== 'login' ? 'login' : 'register';
};

const canSubmit = computed(() => {
  if (activeKey.value === 'register') {
    return formState.sms_code && formState.email;
  }

  return formState.password && formState.email;
});

const confirm = async () => {
  formState.remember = true;
  await onFinish(toRaw(formState));
};

// 打开忘记密码
const onOpenReset = () => {
  trackEvent(TRACK_EVENT.CLICK_LOGIN_FORGET_BTN);
  emits('back');
};

// 发送验证码
const handleSendCode = async () => {
  if (isSend.value) return;

  if (!formState.email || !emailReg.test(formState.email)) {
    message.warning($t('qing-shu-ru-zheng-que-de-you-xiang'));
    return;
  }

  if (!formState.remember) {
    message.warning($t('qing-xian-gou-xuan-fu-wu-gui-fan'));
    return;
  }

  try {
    const res = await getEmailCode({
      email: formState.email,
    });

    if (!res.code) {
      sendCode();
      message.success($t('yan-zheng-ma-fa-song-cheng-gong'));
    }
  } catch (error) {
    console.error('Failed to send verification code:', error);
  }
};

const { $dayjs } = useTime();

const handleGoogleLoginSuccess = async (data: LoginInfo[]) => {
  const newUser = $dayjs().isSame($dayjs(data[0]?.create_time), 'day');

  trackData(
    'google',
    { email: data[0]?.email_address },
    {
      NewUser: newUser ? USER_TYPE.NEW : USER_TYPE.OLD,
      LoginWay: LOGIN_WAY.GOOGLE,
    },
  );

  if (data.length > 1) {
    // 用户账号列表
    setAccountList(data);
    emits('accountSelect', data);
  } else {
    // 用户token
    await setSession(data[0]);
    emitBroadcast(BROADCAST_KEY.REFRESH, { tabId: TAB_ID });
    buriedLogin(data[0]?.user_id);
    const result = await getUserInfo();

    // 用户信息
    await setUserInfo(result.data as any);

    emits('success', result.data);
  }
};

const onFinish = async (values: any) => {
  // 手动校验表单
  const validationErrors: string[] = [];

  if (activeKey.value === 'register') {
    // // 校验邮箱
    // if (!values.email) {
    //   validationErrors.push($t('qing-shu-ru-you-xiang'));
    // } else if (!emailReg.test(values.email)) {
    //   validationErrors.push($t('you-xiang-ge-shi-bu-zheng-que'));
    // }

    // 校验验证码
    if (!values.sms_code) {
      validationErrors.push($t('qing-shu-ru-yan-zheng-ma'));
    }

    // 校验密码
    if (!values.password) {
      validationErrors.push($t('qing-shu-ru-mi-ma'));
    } else if (!PwdValidator.isSafeRange(values.password)) {
      validationErrors.push($t('mi-ma-chang-du-bu-fu-he-yao-qiu'));
    } else if (!PwdValidator.isValidFormat(values.password)) {
      validationErrors.push($t('mi-ma-ge-shi-bu-fu-he-yao-qiu'));
    }

    // 校验确认密码
    if (!values.checkPassword) {
      validationErrors.push($t('qing-que-ren-mi-ma'));
    } else if (values.checkPassword !== values.password) {
      validationErrors.push($t('liang-ci-mi-ma-shu-ru-bu-yi-zhi-qing-zhong-xin-shu-ru'));
    }
  } else {
    // // 校验邮箱
    // if (!values.email) {
    //   validationErrors.push($t('qing-shu-ru-you-xiang'));
    // } else if (!emailReg.test(values.email)) {
    //   validationErrors.push($t('you-xiang-ge-shi-bu-zheng-que'));
    // }

    // 校验密码
    if (!values.password) {
      validationErrors.push($t('qing-shu-ru-mi-ma'));
    }
  }

  // 如果有校验错误，显示第一个错误信息并返回
  if (validationErrors.length > 0) {
    message.error(validationErrors[0]);
    return;
  }

  if (!formState.remember) {
    // message.warning($t('qing-xian-yue-du-bing-gou-xuan-tiao-kuan'))
    showProtocolConfirm(confirm);
    return;
  }
  loading.value = true;

  try {
    let res;

    let newUser = false;

    if (activeKey.value === 'register') {
      newUser = true;
      const [res1, res2] = await concurrency([
        transformPublicKey({
          email: values.email,
          password: values.password,
          verify_code: values.sms_code,
        }),
        getLocation(),
      ]);

      const [encrypted, rsa] = res1!;

      res = await registerByEmail(
        withRegisterOrLoginChannelForUs({
          rsa_key_id: rsa?.id,
          auth_data: encrypted,
          country: res2.country,
        }),
      );
    } else {
      newUser = false;
      const [res1, res2] = await concurrency([
        transformPublicKey({
          email: values.email,
          password: values.password,
        }),
        getLocation(),
      ]);

      const [encrypted, rsa] = res1;

      res = await loginByEmail(
        withRegisterOrLoginChannelForUs({
          rsa_key_id: rsa?.id,
          auth_data: encrypted,
          country: res2.country,
        }),
        {
          autoErrorHandle: false,
        },
      );

      if (
        [ServerErrorCode.USER_EXIST_ERROR, ServerErrorCode.EMAIL_NOT_REGISTERED].includes(res.code)
      ) {
        activeKey.value = 'register';
        throw new Error(res.msg || '登录失败');
      }

      if (res.code !== 0) {
        const errorMsg = res.msg || '登录失败';
        message.error(errorMsg);
        throw new Error(errorMsg);
      }
    }

    if (res.data) {
      const { data } = res;

      // 用户账号列表
      setAccountList(data);

      useUserStore().setAccountList(data as unknown as Account[]);

      // 优先使用后端返回的 is_new_user 字段，如果没有则使用前端判断
      const isNewUser = data[0]?.is_new_user !== undefined ? data[0].is_new_user : newUser;

      trackData(
        'google',
        { email: values.email },
        {
          LoginWay: LOGIN_WAY.EMAIL,
          NewUser: isNewUser ? USER_TYPE.NEW : USER_TYPE.OLD,
          // InviterId: res.data[0]?.invitation_info?.user_id || undefined,
        },
      );

      // 埋点：邮箱登录成功
      trackEvent(TRACK_EVENT.LOGIN_EMAIL_SUCCESS, {
        account_name: values.email,
        NewUser: isNewUser ? USER_TYPE.NEW : USER_TYPE.OLD,
        login_type: activeKey.value,
      });

      if (data.length > 1) {
        emits('accountSelect', data);
      } else {
        // 用户token
        await setSession(res.data[0]);

        emitBroadcast(BROADCAST_KEY.REFRESH, { tabId: TAB_ID });
        buriedLogin(res.data[0]?.user_id);
        const result = await getUserInfo();

        // 用户信息
        setUserInfo(result.data as any);

        emits('success', result.data);
      }
      sessionRemoveItem(SESSION_SOURCE);

      loading.value = false;
    }
  } catch (error: any) {
    console.log(error);
    loading.value = false;
    // 埋点：邮箱登录失败
    const errorCode = error?.code || error?.error_code || 'unknown';
    const errorMsg = error?.msg || error?.error_reason || error?.message || '未知错误';

    // 根据错误类型记录不同的失败埋点
    if (errorCode === 10003) {
      trackEvent(TRACK_EVENT.LOGIN_FAIL_PASSWORD_ERROR, {
        login_type: 'email',
        remaining_num: error?.data?.remaining_num,
      });
    } else if (errorCode === 10002) {
      trackEvent(TRACK_EVENT.LOGIN_FAIL_ACCOUNT_LOCKED, {
        login_type: 'email',
        expired_time: error?.data?.expired_time,
      });
    } else if (
      [ServerErrorCode.USER_EXIST_ERROR, ServerErrorCode.EMAIL_NOT_REGISTERED].includes(errorCode)
    ) {
      trackEvent(TRACK_EVENT.LOGIN_FAIL_ACCOUNT_NOT_EXIST, {
        login_type: 'email',
        error_code: errorCode,
      });
    } else if (errorCode === 'network_error' || error?.message?.includes('network')) {
      trackEvent(TRACK_EVENT.LOGIN_FAIL_NETWORK_ERROR, {
        login_type: 'email',
      });
    } else {
      trackEvent(TRACK_EVENT.LOGIN_EMAIL_FAIL, {
        login_type: activeKey.value,
        error_code: errorCode,
        error_msg: errorMsg,
      });
    }
  }
};
</script>

<style lang="scss" scoped>
@use '@rendora/design/styles/mixins.scss' as *;

.login-form {
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
  width: 328px;
  @include mobile {
    padding-bottom: responsive-size(20px);
    width: responsive-size(335px);
  }
}

.login-form__title {
  font-size: var(--x-text-24-size);
  font-weight: var(--x-weight-medium);
  line-height: var(--x-text-24-line-height);
  text-align: center;
  color: var(--x-text-1);
  margin-bottom: 8px;

  @include mobile {
    margin-bottom: responsive-size(8px);
  }
}

.sub {
  font-size: var(--x-text-12-size);
  line-height: var(--x-text-12-line-height);
  color: var(--x-text-5);

  @include mobile {
    margin-bottom: responsive-size(48px);
  }

  .sub-item {
    display: flex;
    align-items: center;
    gap: 4px;

    @include mobile {
      gap: responsive-size(4px);
    }
  }
}

.login-input {
  border-radius: 8px;
  letter-spacing: 0.3px;
  width: 100%;
}

.login-form__form {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  margin-top: 48px;

  @include mobile {
    margin-bottom: responsive-size(48px);
    gap: responsive-size(16px);
  }

  :deep(.form-item) {
    width: 100%;
    margin-bottom: 0;
  }
}

.login-form__captcha-input {
  flex: 1;
  padding-right: 120px;

  @include mobile {
    padding-right: responsive-size(120px);
  }
}

.login-form__captcha-button {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  white-space: nowrap;
  z-index: 1;
  padding: 0 8px;
  &:hover {
    background-color: var(--x-purple-1);
  }
  &:active:not(:disabled) {
    transform: translateY(-50%) scale(0.97) !important;
  }

  @include mobile {
    right: responsive-size(8px);
    padding: 0 responsive-size(8px);
  }
}

.login-form__buttons {
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 16px;
  margin-top: 32px;

  @include mobile {
    gap: responsive-size(16px);
    margin-top: responsive-size(32px);
  }
}

.login-form__divider {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--x-text-14-size);
  font-weight: var(--x-weight-regular);
  color: var(--x-text-1);
  position: relative;
  width: 100%;

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background-color: var(--x-back-4);
  }

  span {
    padding: 0 12px;

    @include mobile {
      padding: 0 responsive-size(12px);
    }
  }
}

.google-login-btn {
  height: 44px;
  width: 100%;

  @include mobile {
    height: responsive-size(44px);
  }
}

.login-form__forget-password {
  padding: 0;
  line-height: 1;
  height: fit-content;
}

.pwd-rule-tip {
  display: flex;
  margin-top: 12px;
  gap: 4px;

  @include mobile {
    gap: responsive-size(4px);
    margin-top: responsive-size(12px);
  }

  .pwd-rule-tip-icon {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    color: var(--x-error-6);
    transform: translateY(2px);

    @include mobile {
      width: responsive-size(16px);
      height: responsive-size(16px);
      transform: translateY(responsive-size(2px));
    }
  }
}

.pwd-rule-tip--success {
  .pwd-rule-tip-icon {
    color: var(--x-success-6);
  }
}
</style>
